import OffersWidget from 'components/offers';
import { LayoutMode, LayoutProps } from 'components/types';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GetDeliveryPromiseOfferRankingRequest, ProductInfoAndOfferMerchantID, ProductInfoAndOffers } from 'services';
import { selectCustomerPreferenceState, selectSelectedStateCode, setPreferredVisitType } from 'state/ducks/customer-preference/slice';
import {
    addDeliveryPromiseWithState,
    selectDelieryPromiseOfferAdditionalInfoByState,
    selectDeliveryPromiseByAsin,
    selectSortedBuyingOptionsByAsin
} from 'state/ducks/delivery-promise';
import { getDeliveryPromiseOfferRankingByStateSaga } from 'state/ducks/delivery-promise/sagas';
import { selectOfferByAsin, selectOffersState, } from 'state/ducks/offers/slice';
import { selectStringTranslator } from 'state/ducks/translator-strings/slice';
import { selectVariationsState } from 'state/ducks/variations/slice';
import { useAppSelector } from 'state/hooks';
import { getState } from 'state/static-data';
import InitialContext from 'state/static-data/InitialContext';
import { BuyingOption, Offer, Variation, VisitType } from 'state/types';
import DeliveryPromiseUtils from 'state/utils/DeliveryPromiseUtils';
import { TranslatorStringIds } from 'utils';
import './index.scss';
import TwisterVariation, { TwisterVariationProps } from './variation-card';

interface VariationWrapper {
    readonly variation: Variation,
    readonly offer: Offer,
    readonly qualifiedBuyingOptions: BuyingOption[]
}

const resolveSelectedVariation = (qualifiedVariations: VariationWrapper[], preferredVisitType?: VisitType) => {
    if (qualifiedVariations.length === 1) {
        return qualifiedVariations[0];
    }
    return qualifiedVariations.find(v => v.variation.visitType === preferredVisitType);
}

let countDownTimer: NodeJS.Timeout | undefined = undefined;

const TwisterWidget = (props: LayoutProps) => {
    const { layoutMode } = props;
    const dispatch = useDispatch();
    const t = useSelector(selectStringTranslator);
    const selectedStateCode = useSelector(selectSelectedStateCode);
    const offers = useSelector(selectOffersState).offers;
    const selectedState = getState(selectedStateCode);
    const preferredVisitType = useSelector(selectCustomerPreferenceState).preferredVisitType;
    const variationsState = useSelector(selectVariationsState);
    const [qualifiedVariations, setQualifiedVariations] = useState<VariationWrapper[]>();
    const currentStateDeliveryPromisesAndSortedOffers = useAppSelector((state) =>
        selectDelieryPromiseOfferAdditionalInfoByState(state, selectedStateCode ?? "UNKNOWN_STATE"))

    if (layoutMode === LayoutMode.DESKTOP) {
        clearInterval(countDownTimer)
        countDownTimer = setInterval((deliveryPromiseState) => {
            if (deliveryPromiseState) {
                const updatedState = DeliveryPromiseUtils.refreshProviderAvailableStatus(deliveryPromiseState)
                if (JSON.stringify(updatedState) !== JSON.stringify(deliveryPromiseState)) {
                    dispatch(addDeliveryPromiseWithState(updatedState))
                }
            }
        }, 3 * 1000, currentStateDeliveryPromisesAndSortedOffers)
    }

    useEffect(() => {
        // 1. Fetch all buying options from cdp controller.
        // 2. Set qualified buying options without ranking position.
        // 3. Request delivery promise and  rank offer ajax call, update buying options ranking when selectSortedBuyingOptionsByAsin is called.
        const qualifiedVariations: VariationWrapper[] = variationsState.variations
            .map(variation => {
                const offer = selectOfferByAsin(offers, variation.asin);
                const qualifiedBuyingOptions = offer?.buyingOptions
                    .filter(bo => !!selectedState && bo.states.includes(selectedState.stateCode));
                if ((qualifiedBuyingOptions?.length ?? 0) === 0) {
                    // No qualified buying options
                    return;
                }
                return { variation, offer, qualifiedBuyingOptions } as VariationWrapper;
            }).filter(v => !!v) as VariationWrapper[];
        setQualifiedVariations(qualifiedVariations);

        if (!currentStateDeliveryPromisesAndSortedOffers
            && selectedState
            && selectedState.launched
            && qualifiedVariations.length > 0
            // only call once on desktop version.
            && layoutMode === LayoutMode.DESKTOP) {

            const productInfoAndOfferMerchantIDs: ProductInfoAndOfferMerchantID[] = qualifiedVariations.map(v => {
                return {
                    asin: v.variation.asin,
                    visitType: v.variation.visitType,
                    obfuscatedMerchantIds: v.qualifiedBuyingOptions.map(bo => bo.obfuscatedMerchantId)
                }
            });
            const productInfoAndOffers: ProductInfoAndOffers[] = qualifiedVariations.map(v => {
                return {
                    asin: v.variation.asin,
                    offers: v.qualifiedBuyingOptions.map(bo => {
                        return {
                            amount: bo.priceToPay.amount,
                            merchantId: bo.obfuscatedMerchantId
                        }
                    })
                }
            })
            const request: GetDeliveryPromiseOfferRankingRequest = {
                stateCode: selectedState.stateCode,
                productInfoAndOfferMerchantIDs: productInfoAndOfferMerchantIDs,
                productInfoAndOffers: productInfoAndOffers
            }
            dispatch(getDeliveryPromiseOfferRankingByStateSaga(request));
        }
    }, [selectedState]);

    if (!selectedState || !selectedState.launched) {
        return <></>
    }
    if (!qualifiedVariations || qualifiedVariations.length === 0) {
        return (<pui-section flowDirection="horizontal" secondaryAxisArrangement="start">
            <pui-icon imgClass="status-error-icon" spacingRight="mini" />
            <pui-text input={t.resolve(TranslatorStringIds.ConditionUnavailableMessage, {
                condition: InitialContext.getProduct().conditionName,
                location: selectedState.label
            })} encoded />
        </pui-section>)
    }
    if (!currentStateDeliveryPromisesAndSortedOffers) {
        return <></>
    }

    const selectedVariation = resolveSelectedVariation(qualifiedVariations, preferredVisitType);
    const selectedVariationDeliveryPromise = selectDeliveryPromiseByAsin(currentStateDeliveryPromisesAndSortedOffers, selectedVariation?.variation.asin);
    const selectedVariationSortedBuyingOptions = selectSortedBuyingOptionsByAsin(
        currentStateDeliveryPromisesAndSortedOffers,
        selectedVariation?.variation.asin,
        selectedVariation?.qualifiedBuyingOptions);
    const onVariationClick = (visitType: VisitType) => {
        dispatch(setPreferredVisitType(visitType));
    }

    const variationElements = (() => {
        const twisterVariations: TwisterVariationProps[] = qualifiedVariations
            .map(wrapper => {

                const variationDeliveryPromise = selectDeliveryPromiseByAsin(currentStateDeliveryPromisesAndSortedOffers, wrapper.variation.asin);
                const { variation, offer, qualifiedBuyingOptions } = wrapper;

                const twisterVariationProps: TwisterVariationProps = {
                    visitType: variation.visitType,
                    title: variation.label,
                    priceLabel: offer.offerSummary.label,
                    qualifiedBuyingOptions: qualifiedBuyingOptions,
                    merchantDeliveryPromises: variationDeliveryPromise?.merchantDeliveryPromises ?? [],
                    deliveryPromiseTimes: variationDeliveryPromise?.merchantDeliveryPromises ?? [],
                    isSelected: selectedVariation?.variation.visitType === variation.visitType,
                }
                return twisterVariationProps;
            }).filter(variation => !!variation);

        //Make sure async always appear first than sync
        if (twisterVariations.length > 1 && !(twisterVariations[0].visitType === 'asynchronous')) {
            [twisterVariations[0], twisterVariations[1]] = [twisterVariations[1], twisterVariations[0]]
        }

        const variationElements = [];
        // Currently we only have sync and async type
        for (let i = 0; i < twisterVariations.length; i++) {
            const props = twisterVariations[i];
            !!(variationElements.length) && !!props && variationElements.push(<div className='spacer' key={`variation-spacer-${i}`}></div>)
            !!props && variationElements.push(<TwisterVariation {...props}
                onClick={() => onVariationClick(props.visitType)}
                key={`variation-${i}`} />)
        }
        return variationElements;
    })();

    return (
        <>
            <pui-divider />
            <pui-section spacingTop="medium" class="twister-box">
                <pui-heading input={'Choose a visit type'} spacingBottom="medium" id="choose-visit-type"></pui-heading>
                <div className='variations-widget' role="group" aria-labelledby="choose-visit-type">
                    {variationElements}
                </div>
                {selectedVariation?.variation.shortDescription && <pui-section spacingTop="medium">
                    <pui-text input={selectedVariation?.variation.shortDescription} encoded />
                </pui-section>}
            </pui-section>
            <OffersWidget
                variation={selectedVariation?.variation}
                buyingOptions={selectedVariationSortedBuyingOptions ?? []}
                merchantDeliveryPromises={selectedVariationDeliveryPromise?.merchantDeliveryPromises ?? []}
                selectedStateCode={selectedStateCode ?? ''}
                {...props} />
        </>
    )
}
export default TwisterWidget;